import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { ResizableImageWrapper } from './ResizableImageWrapper';
import { ResizableImageAttributes, DragState } from './types';

/**
 * ResizableImageNodeView Component
 * 
 * React NodeView component for resizable images in TipTap.
 * Provides drag handles, white border on selection, and aspect ratio preservation.
 */
export const ResizableImageNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  extension,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    aspectRatio: 1,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalDimensions, setNaturalDimensions] = useState({ width: 0, height: 0 });

  const attrs = node.attrs as ResizableImageAttributes;
  const options = extension.options;

  // Debug logging for selection state
  useEffect(() => {
    console.log('[ResizableImageNodeView] Selection state changed:', {
      selected,
      nodeType: node.type.name,
      nodeAttrs: attrs,
      imageLoaded,
      timestamp: new Date().toISOString()
    });
  }, [selected, node.type.name, attrs, imageLoaded]);

  // Debug logging for component mount/unmount
  useEffect(() => {
    console.log('[ResizableImageNodeView] Component mounted for image:', attrs.src);
    return () => {
      console.log('[ResizableImageNodeView] Component unmounting for image:', attrs.src);
    };
  }, [attrs.src]);

  // Handle image load to get natural dimensions and set initial size
  const handleImageLoad = useCallback(() => {
    if (!imageRef.current) return;

    const { naturalWidth, naturalHeight } = imageRef.current;
    setNaturalDimensions({ width: naturalWidth, height: naturalHeight });
    setImageLoaded(true);

    // Set initial dimensions if not already set
    if (!attrs.width && !attrs.height) {
      const aspectRatio = naturalWidth / naturalHeight;
      const maxWidth = Math.min(naturalWidth, options.maxWidth || 800);
      const width = maxWidth;
      const height = Math.round(width / aspectRatio);

      updateAttributes({
        width,
        height,
      });
    }
  }, [attrs.width, attrs.height, options.maxWidth, updateAttributes]);

  // Calculate current dimensions
  const currentWidth = attrs.width || naturalDimensions.width || 400;
  const currentHeight = attrs.height || naturalDimensions.height || 300;
  const aspectRatio = naturalDimensions.width && naturalDimensions.height 
    ? naturalDimensions.width / naturalDimensions.height 
    : currentWidth / currentHeight;

  // Handle resize start
  const handleResizeStart = useCallback((clientX: number, clientY: number) => {
    setDragState({
      isDragging: true,
      startX: clientX,
      startY: clientY,
      startWidth: currentWidth,
      startHeight: currentHeight,
      aspectRatio,
    });
  }, [currentWidth, currentHeight, aspectRatio]);

  // Handle resize during drag
  const handleResize = useCallback((clientX: number, clientY: number) => {
    if (!dragState.isDragging) return;

    const deltaX = clientX - dragState.startX;
    const deltaY = clientY - dragState.startY;
    
    // Calculate new width based on horizontal movement
    let newWidth = dragState.startWidth + deltaX;
    
    // Apply constraints
    const minWidth = options.minWidth || 50;
    const maxWidth = Math.min(options.maxWidth || 1200, naturalDimensions.width || 1200);
    
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    
    // Calculate height maintaining aspect ratio
    let newHeight = newWidth / dragState.aspectRatio;
    
    // Apply height constraints
    const minHeight = options.minHeight || 50;
    const maxHeight = Math.min(options.maxHeight || 800, naturalDimensions.height || 800);
    
    newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    
    // Recalculate width if height was constrained
    if (newHeight !== newWidth / dragState.aspectRatio) {
      newWidth = newHeight * dragState.aspectRatio;
    }

    // Update attributes with new dimensions and mark as manually resized
    updateAttributes({
      width: Math.round(newWidth),
      height: Math.round(newHeight),
      hasBeenResized: true,
    });
  }, [dragState, options, naturalDimensions, updateAttributes]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setDragState(prev => ({ ...prev, isDragging: false }));
  }, []);

  // Mouse event handlers
  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleResize(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleResizeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.isDragging, handleResize, handleResizeEnd]);

  return (
    <NodeViewWrapper className="resizable-image-nodeview">
      <ResizableImageWrapper
        src={attrs.src || ''}
        alt={attrs.alt || ''}
        title={attrs.title || ''}
        width={attrs.width}
        height={attrs.height}
        hasBeenResized={attrs.hasBeenResized}
        selected={selected}
        isDragging={dragState.isDragging}
        onResizeStart={handleResizeStart}
        onImageLoad={handleImageLoad}
        onImageRef={(ref) => {
          if (ref !== imageRef.current) {
            (imageRef as React.MutableRefObject<HTMLImageElement | null>).current = ref;
          }
        }}
        imageLoaded={imageLoaded}
        maxWidth={options.maxWidth}
      />
    </NodeViewWrapper>
  );
}; 